import { Cancel, Menu } from 'iconoir-react';
import { useCallback } from 'react';

import { ProfileIcon } from '@american-autonomy/rantizo-ui';
import { ActionIcon, Container, Drawer, Group, Header, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import DesktopNavigationDropdown from 'components/DesktopNavigationDropdown';
import DesktopNavigationDropdownOnClick from 'components/DesktopNavigationDropdownOnClick';
import HamburgerMenuDropdown from 'components/HamburgerMenuDropdown';
import Logo from 'components/Logo';
import WithFeatureAccess from 'components/WithFeatureAccess';
import CallToActionNavigationLink from 'components/links/CallToActionNavigationLink';
import DesktopNavigationDropdownLink from 'components/links/DesktopNavigationDropdownLink';
import HelpLink from 'components/links/HelpLink';
import LegalNavigationLink from 'components/links/LegalNavigationLink';
import MainNavigationLink from 'components/links/MainNavigationLink';

import useDevice from 'hooks/useDevice';
import useFeatureAccess from 'hooks/useFeatureAccess';
import { AvailableFeatures } from 'hooks/useFeatureAccess/constants';
import useFeatureFlags from 'hooks/useFeatureFlags';
import usePageRoutes from 'hooks/usePageRoutes';
import useRoles from 'hooks/useRoles';

import useTranslation from './hooks/useTranslation';

import {
    ACCOUNT_TEST_ID,
    CUSTOMERS_PAGE_TEST_ID,
    HAMBURGER_MENU_TEST_ID,
    LOGOUT_TEST_ID,
    MANAGE_CONNECTIONS_TEST_ID,
    MANAGE_EQUIPMENT_TEST_ID,
    MANAGE_ORGANIZATION_TEST_ID,
    MY_ACCOUNT,
    SERVICE_OPERATOR_MAP_TEST_ID,
    TEST_ID,
    V2_FAA_REPORTS_PATH,
    V2_FLIGHTS_AND_MAPS_PATH,
    V2_PRODUCT_USAGE_REPORTS_PATH,
    V2_WORK_ORDERS_PATH
} from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const SiteHeader: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { canManageOrganizations } = useRoles();
    const { devices } = useDevice();

    const { deviceType } = devices;
    const {
        ACCOUNT_DRAWER_LAYOUT,
        CUSTOMERS: CUSTOMERS_FF,
        FAA_REPORTS: FAA_REPORTS_FEATURE_FLAG,
        FLIGHT_LOG_LIST,
        MANAGE_CONNECTIONS: MANAGE_CONNECTIONS_FEATURE_FLAG,
        OPERATOR_MAP_TILE,
        V2_RESKIN,
        V2_RESKIN_CUTOFF
    } = useFeatureFlags();
    const useV2Routes = V2_RESKIN_CUTOFF && V2_RESKIN;
    const {
        ACCOUNT,
        COPYRIGHT,
        CREATE_PRODUCT_USAGE_REPORT,
        CREATE_WORK_ORDER,
        CUSTOMERS,
        FAA_REPORTS,
        FLIGHT_LOG,
        LOGOUT,
        MANAGE_CONNECTIONS,
        MANAGE_EQUIPMENT,
        MANAGE_ORGANIZATION,
        MY_ORGANIZATION,
        PRODUCT_USAGE_REPORTS,
        SERVICE_OPERATOR_MAP,
        TERMS_OF_SERVICE,
        WORK_ORDERS
    } = useTranslation();
    const {
        accountDrawerPage,
        accountLogoutPage,
        accountPage,
        customersPage,
        faaReportsPage,
        flightLogPage,
        homePage,
        manageConnectionsPage,
        manageOrganizationPage,
        operatorMapPage,
        organizationEquipment,
        productUsageReportPageWithModal,
        productUsageReportsPage,
        termsOfServicePage,
        uploadFlightLogPage,
        workOrderPage
    } = usePageRoutes();
    const [opened, { close, toggle }] = useDisclosure(false);
    const currentYear = new Date().getFullYear().toString();

    const featureAccess = useFeatureAccess();

    const handleNavigation = useCallback(() => {
        if (deviceType === 'mobile') {
            close();
        }
    }, [deviceType, close]);

    const v1FlightLogPath = FLIGHT_LOG_LIST ? flightLogPage : uploadFlightLogPage;
    const flightLogPath = useV2Routes ? V2_FLIGHTS_AND_MAPS_PATH : v1FlightLogPath;
    const workOrdersPath = useV2Routes ? V2_WORK_ORDERS_PATH : homePage;
    const productUsageReportsPath = useV2Routes
        ? V2_PRODUCT_USAGE_REPORTS_PATH
        : productUsageReportsPage;
    const faaReportsPath = useV2Routes ? V2_FAA_REPORTS_PATH : faaReportsPage;

    const accountPageRoute = ACCOUNT_DRAWER_LAYOUT ? accountDrawerPage(MY_ACCOUNT) : accountPage;

    return (
        <Header className={styles.navbarTopContainer} data-testid={testId} height={rem(60)}>
            <Container className={styles.navbarTop}>
                <Logo className={styles.logo} />

                <Group className={styles.navLinkGroup}>
                    <DesktopNavigationDropdown
                        dropdownElement={
                            <WithFeatureAccess feature={AvailableFeatures.WorkOrders}>
                                <MainNavigationLink link={workOrdersPath} text={WORK_ORDERS} />
                            </WithFeatureAccess>
                        }
                    >
                        {featureAccess[AvailableFeatures.WorkOrders] && (
                            <DesktopNavigationDropdownLink
                                link={workOrderPage}
                                text={CREATE_WORK_ORDER}
                            />
                        )}
                    </DesktopNavigationDropdown>

                    <DesktopNavigationDropdown
                        dropdownElement={
                            <WithFeatureAccess feature={AvailableFeatures.ProductUsageReports}>
                                <MainNavigationLink
                                    link={productUsageReportsPath}
                                    text={PRODUCT_USAGE_REPORTS}
                                />
                            </WithFeatureAccess>
                        }
                    >
                        {featureAccess[AvailableFeatures.ProductUsageReports] && (
                            <DesktopNavigationDropdownLink
                                link={productUsageReportPageWithModal}
                                text={CREATE_PRODUCT_USAGE_REPORT}
                            />
                        )}
                    </DesktopNavigationDropdown>

                    <WithFeatureAccess feature={AvailableFeatures.FlightLogs}>
                        <MainNavigationLink link={flightLogPath} text={FLIGHT_LOG} />
                    </WithFeatureAccess>

                    {FAA_REPORTS_FEATURE_FLAG && (
                        <WithFeatureAccess feature={AvailableFeatures.FaaReports}>
                            <MainNavigationLink link={faaReportsPath} text={FAA_REPORTS} />
                        </WithFeatureAccess>
                    )}

                    <HelpLink />

                    <DesktopNavigationDropdownOnClick dropdownElement={<ProfileIcon />}>
                        <DesktopNavigationDropdownLink
                            link={accountPageRoute}
                            testId={ACCOUNT_TEST_ID}
                            text={ACCOUNT}
                        />

                        <DesktopNavigationDropdownLink
                            link={manageOrganizationPage}
                            testId={MANAGE_ORGANIZATION_TEST_ID}
                            text={canManageOrganizations ? MANAGE_ORGANIZATION : MY_ORGANIZATION}
                        />

                        {MANAGE_CONNECTIONS_FEATURE_FLAG && (
                            <DesktopNavigationDropdownLink
                                link={manageConnectionsPage()}
                                testId={MANAGE_CONNECTIONS_TEST_ID}
                                text={MANAGE_CONNECTIONS}
                            />
                        )}

                        <DesktopNavigationDropdownLink
                            link={organizationEquipment}
                            testId={MANAGE_EQUIPMENT_TEST_ID}
                            text={MANAGE_EQUIPMENT}
                        />

                        {OPERATOR_MAP_TILE && (
                            <DesktopNavigationDropdownLink
                                link={operatorMapPage}
                                testId={SERVICE_OPERATOR_MAP_TEST_ID}
                                text={SERVICE_OPERATOR_MAP}
                            />
                        )}

                        {CUSTOMERS_FF && (
                            <DesktopNavigationDropdownLink
                                link={customersPage}
                                testId={CUSTOMERS_PAGE_TEST_ID}
                                text={CUSTOMERS}
                            />
                        )}

                        <DesktopNavigationDropdownLink
                            link={accountLogoutPage}
                            testId={LOGOUT_TEST_ID}
                            text={LOGOUT}
                        />
                    </DesktopNavigationDropdownOnClick>
                </Group>

                <div className={styles.menuButton}>
                    <ActionIcon
                        className={styles.menuIcon}
                        data-testid={HAMBURGER_MENU_TEST_ID}
                        onClick={toggle}
                    >
                        {
                            // @ts-expect-error implementation will later be changed
                            <Menu strokeWidth={1.75} />
                        }
                    </ActionIcon>
                </div>

                <Drawer.Root
                    className={styles.drawer}
                    onClose={close}
                    opened={opened}
                    position="right"
                    size="sm"
                    zIndex={1100}
                >
                    <Drawer.Overlay opacity={0.15} />

                    <Drawer.Content
                        className={styles.sidebarContainer}
                        data-testid="mobile-sidebar"
                    >
                        <Drawer.Header className={styles.sidebarHeader}>
                            <Drawer.CloseButton
                                className={styles.sidebarClose}
                                color="purple"
                                data-testid="drawer-close"
                                size="md"
                            >
                                {
                                    // @ts-expect-error implementation will later be changed
                                    <Cancel strokeWidth={2} />
                                }
                            </Drawer.CloseButton>
                        </Drawer.Header>

                        <Drawer.Body className={styles.sidebar}>
                            <div className={styles.sidebarMainLinkGroup}>
                                <HamburgerMenuDropdown
                                    dropdownElement={
                                        <WithFeatureAccess feature={AvailableFeatures.WorkOrders}>
                                            <MainNavigationLink
                                                link={workOrdersPath}
                                                onClick={handleNavigation}
                                                text={WORK_ORDERS}
                                            />
                                        </WithFeatureAccess>
                                    }
                                >
                                    {featureAccess[AvailableFeatures.WorkOrders] && (
                                        <CallToActionNavigationLink
                                            link={workOrderPage}
                                            onClick={handleNavigation}
                                            text={CREATE_WORK_ORDER}
                                        />
                                    )}
                                </HamburgerMenuDropdown>

                                <HamburgerMenuDropdown
                                    dropdownElement={
                                        <WithFeatureAccess
                                            feature={AvailableFeatures.ProductUsageReports}
                                        >
                                            <MainNavigationLink
                                                link={productUsageReportsPath}
                                                onClick={handleNavigation}
                                                text={PRODUCT_USAGE_REPORTS}
                                            />
                                        </WithFeatureAccess>
                                    }
                                >
                                    {featureAccess[AvailableFeatures.ProductUsageReports] && (
                                        <CallToActionNavigationLink
                                            link={productUsageReportPageWithModal}
                                            onClick={handleNavigation}
                                            text={CREATE_PRODUCT_USAGE_REPORT}
                                        />
                                    )}
                                </HamburgerMenuDropdown>

                                <WithFeatureAccess feature={AvailableFeatures.FlightLogs}>
                                    <MainNavigationLink
                                        link={flightLogPath}
                                        onClick={handleNavigation}
                                        text={FLIGHT_LOG}
                                    />
                                </WithFeatureAccess>

                                {FAA_REPORTS_FEATURE_FLAG && (
                                    <WithFeatureAccess feature={AvailableFeatures.FaaReports}>
                                        <MainNavigationLink
                                            link={faaReportsPath}
                                            text={FAA_REPORTS}
                                        />
                                    </WithFeatureAccess>
                                )}

                                <HelpLink />
                            </div>

                            <div>
                                <div className={styles.sidebarCTALinkGroup}>
                                    <ProfileIcon />

                                    <CallToActionNavigationLink
                                        link={accountPageRoute}
                                        onClick={handleNavigation}
                                        testId={ACCOUNT_TEST_ID}
                                        text={ACCOUNT}
                                    />

                                    <CallToActionNavigationLink
                                        text={
                                            canManageOrganizations
                                                ? MANAGE_ORGANIZATION
                                                : MY_ORGANIZATION
                                        }
                                        link={manageOrganizationPage}
                                        onClick={handleNavigation}
                                        testId={MANAGE_ORGANIZATION_TEST_ID}
                                    />

                                    {MANAGE_CONNECTIONS_FEATURE_FLAG && (
                                        <CallToActionNavigationLink
                                            link={manageConnectionsPage()}
                                            testId={MANAGE_CONNECTIONS_TEST_ID}
                                            text={MANAGE_CONNECTIONS}
                                        />
                                    )}

                                    <CallToActionNavigationLink
                                        link={organizationEquipment}
                                        onClick={handleNavigation}
                                        testId={MANAGE_EQUIPMENT_TEST_ID}
                                        text={MANAGE_EQUIPMENT}
                                    />

                                    {OPERATOR_MAP_TILE && (
                                        <CallToActionNavigationLink
                                            link={operatorMapPage}
                                            testId={SERVICE_OPERATOR_MAP_TEST_ID}
                                            text={SERVICE_OPERATOR_MAP}
                                        />
                                    )}

                                    {CUSTOMERS_FF && (
                                        <CallToActionNavigationLink
                                            link={customersPage}
                                            testId={CUSTOMERS_PAGE_TEST_ID}
                                            text={CUSTOMERS}
                                        />
                                    )}

                                    <CallToActionNavigationLink
                                        link={accountLogoutPage}
                                        onClick={handleNavigation}
                                        testId={LOGOUT_TEST_ID}
                                        text={LOGOUT}
                                    />
                                </div>

                                <div className={styles.sidebarLegalLinkGroup}>
                                    <LegalNavigationLink
                                        link={termsOfServicePage}
                                        onClick={handleNavigation}
                                        text={TERMS_OF_SERVICE}
                                    />
                                </div>

                                <p className={styles.copyright}>{COPYRIGHT(currentYear)}</p>
                            </div>
                        </Drawer.Body>
                    </Drawer.Content>
                </Drawer.Root>
            </Container>
        </Header>
    );
};

export default SiteHeader;
